import type { ContractGenerationTemplateFieldMetadata } from '@/api';
import type { TemplateFieldMetadataMap } from '@/types/contractGeneration/wizard';

/**
 * How a template field reads when nobody has named it: the tag's last segment,
 * word-cased — `monthly_fee` → "Monthly Fee". This is the single copy of the
 * rule; the wizard, the field-guidance page and the variant detail page all
 * fall back to it, so an author sees exactly the label a user would.
 */
export function humanizeKey(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

/** `services[].monthly_fee` → `monthly_fee`; `client` → `client`. */
export function lastPathSegment(path: string): string {
  const segment = path.split('.').pop() ?? path;
  return segment.replace(/\[\]$/, '');
}

/**
 * The wizard addresses fields by their runtime path — `services[0].fee`, one
 * per list row — while authored metadata is keyed by the schema path with the
 * index left out, `services[].fee`. Keys are Jinja identifiers, so the only
 * brackets in a path are these markers.
 */
export function normalizeMetadataPath(runtimePath: string): string {
  return runtimePath.replace(/\[\d+\]/g, '[]');
}

/**
 * The join rule the backend walks the schema with: a root field's path is its
 * key; a child hangs off its parent with a dot, and list-item children hang
 * off the list's `[]` marker (`services[]` + `fee` → `services[].fee`).
 */
export function childMetadataPath(parentPath: string, key: string): string {
  return parentPath ? `${parentPath}.${key}` : key;
}

export function lookupFieldMetadata(
  metadata: TemplateFieldMetadataMap | null | undefined,
  runtimePath: string
): ContractGenerationTemplateFieldMetadata | null {
  if (!metadata) return null;
  return metadata[normalizeMetadataPath(runtimePath)] ?? null;
}

/** The authored label when there is one, else today's humanized key. */
export function resolveFieldLabel(
  metadata: Pick<ContractGenerationTemplateFieldMetadata, 'label'> | null | undefined,
  fallbackKey: string
): string {
  const label = metadata?.label?.trim();
  if (label) return label;
  return humanizeKey(fallbackKey);
}

/** Authored prose, trimmed; blank and missing both read as "nothing written". */
export function resolveFieldProse(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/** Whether any of the prose columns carries text — an order alone is not writing. */
export function hasAuthoredProse(
  value:
    | Partial<Pick<ContractGenerationTemplateFieldMetadata, 'label' | 'description' | 'guidance'>>
    | null
    | undefined
): boolean {
  if (!value) return false;
  return [value.label, value.description, value.guidance].some(
    text => typeof text === 'string' && text.trim().length > 0
  );
}

/**
 * Siblings in the order an author arranged them. Each entry with metadata
 * sorts by its `sort_position` — the backend resolves that per sibling group,
 * so `parentPath` must be the group's own parent (the list's `[]` marker for
 * list-item fields, not the list's path). Entries without metadata keep their
 * document order after the arranged ones; with no map at all the input comes
 * back untouched, which is exactly the order the wizard has always used.
 */
export function orderSiblings<T>(
  entries: [string, T][],
  parentPath: string,
  metadata: TemplateFieldMetadataMap | null | undefined
): [string, T][] {
  if (!metadata) return entries;
  const parent = normalizeMetadataPath(parentPath);
  return entries
    .map(([key, value], index) => ({
      key,
      value,
      index,
      position:
        lookupFieldMetadata(metadata, childMetadataPath(parent, key))?.sort_position ?? null,
    }))
    .sort((a, b) => {
      if (a.position === null || b.position === null) {
        if (a.position === b.position) return a.index - b.index;
        return a.position === null ? 1 : -1;
      }
      return a.position - b.position || a.index - b.index;
    })
    .map(({ key, value }): [string, T] => [key, value]);
}
